import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';

export default class CdnCacheService extends Service {
  @service('graphql-optimizer') graphqlOptimizer;

  @tracked cacheHeaders = new Map();
  @tracked invalidationQueue = [];

  // CDN Cache TTL configurations
  CACHE_DURATIONS = {
    static: 31536000, // 1 year for static assets
    content: 3600, // 1 hour for content
    api: 300, // 5 minutes for API responses
    dynamic: 60, // 1 minute for dynamic content
    personalized: 0, // No caching for personalized content
  };

  // CDN providers configuration
  CDN_CONFIG = {
    cloudflare: {
      purgeEndpoint:
        'https://api.cloudflare.com/client/v4/zones/{zone_id}/purge_cache',
      headers: {
        'X-Auth-Email': this.getEnvVar('CLOUDFLARE_EMAIL'),
        'X-Auth-Key': this.getEnvVar('CLOUDFLARE_API_KEY'),
        'Content-Type': 'application/json',
      },
    },
    aws: {
      // AWS CloudFront invalidation would go here
      distributionId: this.getEnvVar('CLOUDFRONT_DISTRIBUTION_ID'),
    },
    fastly: {
      // Fastly configuration would go here
      serviceId: this.getEnvVar('FASTLY_SERVICE_ID'),
      apiKey: this.getEnvVar('FASTLY_API_KEY'),
    },
  };

  /**
   * Gets appropriate cache headers for a GraphQL response
   * @param {string} query - GraphQL query string
   * @param {Object} variables - Query variables
   * @param {Object} result - Query result
   * @returns {Object} Cache headers
   */
  getCacheHeaders(query, variables, result) {
    const cacheType = this.determineCacheType(query, variables, result);
    const ttl = this.CACHE_DURATIONS[cacheType];

    const headers = {
      'Cache-Control': this.buildCacheControlHeader(cacheType, ttl),
      Vary: 'Accept, Accept-Encoding, Authorization',
      'X-Cache-Type': cacheType,
      'X-Cache-TTL': ttl,
    };

    // Add ETag for cache validation
    if (result && cacheType !== 'personalized') {
      headers['ETag'] = this.generateETag(query, variables, result);
    }

    // Add Last-Modified for content with timestamps
    if (result?.publishedDate || result?.updatedAt) {
      const lastModified = result.publishedDate || result.updatedAt;
      headers['Last-Modified'] = new Date(lastModified).toUTCString();
    }

    // Add cache tags for selective invalidation
    headers['Cache-Tag'] = this.generateCacheTags(query, variables, result);

    return headers;
  }

  /**
   * Determines the appropriate cache type for a query
   * @param {string} query - GraphQL query string
   * @param {Object} variables - Query variables
   * @param {Object} result - Query result
   * @returns {string} Cache type
   */
  determineCacheType(query, variables, result) {
    // No caching for authenticated/personalized requests
    if (this.isPersonalizedQuery(query, variables)) {
      return 'personalized';
    }

    // Static content (rarely changes)
    if (this.isStaticContent(query, result)) {
      return 'static';
    }

    // Content that changes occasionally
    if (this.isContentQuery(query)) {
      return 'content';
    }

    // API responses with moderate change frequency
    if (this.isApiQuery(query)) {
      return 'api';
    }

    // Dynamic content (frequently changing)
    return 'dynamic';
  }

  /**
   * Checks if a query is personalized (user-specific)
   * @param {string} query - GraphQL query string
   * @param {Object} variables - Query variables
   * @returns {boolean} Whether query is personalized
   */
  isPersonalizedQuery(query, variables) {
    // Check for user-specific fields
    const personalizedFields = [
      'currentUser',
      'myBookmarks',
      'myComments',
      'userPreferences',
      'notifications',
    ];

    return (
      personalizedFields.some((field) => query.includes(field)) ||
      variables.userId ||
      variables.userToken
    );
  }

  /**
   * Checks if query returns static content
   * @param {string} query - GraphQL query string
   * @param {Object} result - Query result
   * @returns {boolean} Whether content is static
   */
  isStaticContent(query, result) {
    // Static queries typically fetch configuration, metadata, or rarely-changing data
    const staticQueries = [
      'siteConfiguration',
      'gamesList',
      'categoriesList',
      'teamsList',
    ];

    return (
      staticQueries.some((staticQuery) => query.includes(staticQuery)) ||
      (result && this.isOldContent(result))
    );
  }

  /**
   * Checks if content is old (hasn't changed recently)
   * @param {Object} result - Query result
   * @returns {boolean} Whether content is old
   */
  isOldContent(result) {
    if (!result?.publishedDate && !result?.updatedAt) {
      return false;
    }

    const contentDate = new Date(result.publishedDate || result.updatedAt);
    const daysSinceUpdate =
      (Date.now() - contentDate.getTime()) / (1000 * 60 * 60 * 24);

    return daysSinceUpdate > 30; // Content older than 30 days
  }

  /**
   * Checks if query is for content (articles, posts, etc.)
   * @param {string} query - GraphQL query string
   * @returns {boolean} Whether query is for content
   */
  isContentQuery(query) {
    const contentQueries = [
      'articleCollection',
      'reviewArticleCollection',
      'gameArticleCollection',
      'newsArticleCollection',
    ];

    return contentQueries.some((contentQuery) => query.includes(contentQuery));
  }

  /**
   * Checks if query is a general API query
   * @param {string} query - GraphQL query string
   * @returns {boolean} Whether query is API-related
   */
  isApiQuery(query) {
    // API queries are typically collection queries or searches
    return (
      query.includes('Collection') ||
      query.includes('search') ||
      query.includes('tournament') ||
      query.includes('match')
    );
  }

  /**
   * Builds Cache-Control header value
   * @param {string} cacheType - Type of cache
   * @param {number} ttl - Time to live in seconds
   * @returns {string} Cache-Control header value
   */
  buildCacheControlHeader(cacheType, ttl) {
    if (cacheType === 'personalized') {
      return 'private, no-cache, no-store, must-revalidate';
    }

    const directives = ['public'];

    if (ttl > 0) {
      directives.push(`max-age=${ttl}`);

      // Add stale-while-revalidate for better UX
      if (cacheType === 'content') {
        directives.push('stale-while-revalidate=3600'); // 1 hour SWR
      } else if (cacheType === 'api') {
        directives.push('stale-while-revalidate=300'); // 5 minutes SWR
      }
    }

    return directives.join(', ');
  }

  /**
   * Generates ETag for cache validation
   * @param {string} query - GraphQL query string
   * @param {Object} variables - Query variables
   * @param {Object} result - Query result
   * @returns {string} ETag value
   */
  generateETag(query, variables, result) {
    const content = JSON.stringify({ query, variables, result });
    return `"${this.simpleHash(content)}"`;
  }

  /**
   * Generates cache tags for selective invalidation
   * @param {string} query - GraphQL query string
   * @param {Object} variables - Query variables
   * @param {Object} result - Query result
   * @returns {string} Comma-separated cache tags
   */
  generateCacheTags(query, variables, result) {
    const tags = ['graphql'];

    // Add query-based tags
    if (query.includes('articleCollection')) {
      tags.push('articles');
    }
    if (query.includes('gameArticleCollection')) {
      tags.push('game-articles');
    }
    if (query.includes('reviewArticleCollection')) {
      tags.push('review-articles');
    }
    if (query.includes('tournament')) {
      tags.push('tournaments');
    }

    // Add content-based tags
    if (result?.slug) {
      tags.push(`content:${result.slug}`);
    }
    if (result?.items) {
      result.items.forEach((item) => {
        if (item.slug) {
          tags.push(`content:${item.slug}`);
        }
      });
    }

    return tags.join(', ');
  }

  /**
   * Invalidates CDN cache for specific tags or URLs
   * @param {Array|string} targets - Cache tags or URLs to invalidate
   * @param {string} provider - CDN provider ('cloudflare', 'aws', 'fastly')
   * @returns {Promise<boolean>} Success status
   */
  async invalidateCache(targets, provider = 'cloudflare') {
    if (!Array.isArray(targets)) {
      targets = [targets];
    }

    try {
      switch (provider) {
        case 'cloudflare':
          return await this.invalidateCloudflare(targets);
        case 'aws':
          return await this.invalidateCloudFront(targets);
        case 'fastly':
          return await this.invalidateFastly(targets);
        default:
          console.warn(`Unknown CDN provider: ${provider}`);
          return false;
      }
    } catch (error) {
      console.error(`CDN cache invalidation failed for ${provider}:`, error);
      return false;
    }
  }

  /**
   * Invalidates Cloudflare cache
   * @param {Array} targets - Tags or URLs to invalidate
   * @returns {Promise<boolean>} Success status
   */
  async invalidateCloudflare(targets) {
    const config = this.CDN_CONFIG.cloudflare;
    if (!config.headers['X-Auth-Key'] || !config.headers['X-Auth-Email']) {
      console.warn('Cloudflare credentials not configured');
      return false;
    }

    const payload = {
      tags: targets.filter((t) => !t.startsWith('http')),
      files: targets.filter((t) => t.startsWith('http')),
    };

    const response = await fetch(config.purgeEndpoint, {
      method: 'POST',
      headers: config.headers,
      body: JSON.stringify(payload),
    });

    const result = await response.json();

    if (result.success) {
      console.info(`Cloudflare cache invalidated for: ${targets.join(', ')}`);
      return true;
    } else {
      console.error('Cloudflare cache invalidation failed:', result.errors);
      return false;
    }
  }

  /**
   * Invalidates AWS CloudFront cache
   * @param {Array} targets - Paths to invalidate
   * @returns {Promise<boolean>} Success status
   */
  async invalidateCloudFront(targets) {
    // This would require AWS SDK integration
    console.warn('AWS CloudFront invalidation not implemented yet');
    return false;
  }

  /**
   * Invalidates Fastly cache
   * @param {Array} targets - Tags to invalidate
   * @returns {Promise<boolean>} Success status
   */
  async invalidateFastly(targets) {
    // This would require Fastly API integration
    console.warn('Fastly cache invalidation not implemented yet');
    return false;
  }

  /**
   * Queues cache invalidation to avoid overwhelming CDN APIs
   * @param {Array|string} targets - Targets to invalidate
   * @param {string} provider - CDN provider
   */
  queueInvalidation(targets, provider = 'cloudflare') {
    if (!Array.isArray(targets)) {
      targets = [targets];
    }

    this.invalidationQueue.push(
      ...targets.map((target) => ({ target, provider })),
    );

    // Process queue with debouncing
    this.scheduleQueueProcessing();
  }

  /**
   * Schedules queue processing with debouncing
   */
  scheduleQueueProcessing() {
    if (this._queueTimer) {
      clearTimeout(this._queueTimer);
    }

    this._queueTimer = setTimeout(async () => {
      await this.processInvalidationQueue();
      this._queueTimer = null;
    }, 5000); // 5 second debounce
  }

  /**
   * Processes queued cache invalidations
   */
  async processInvalidationQueue() {
    if (this.invalidationQueue.length === 0) return;

    // Group by provider
    const byProvider = this.invalidationQueue.reduce((acc, item) => {
      if (!acc[item.provider]) {
        acc[item.provider] = [];
      }
      acc[item.provider].push(item.target);
      return acc;
    }, {});

    // Clear queue
    this.invalidationQueue = [];

    // Process each provider
    for (const [provider, targets] of Object.entries(byProvider)) {
      // Remove duplicates
      const uniqueTargets = [...new Set(targets)];
      await this.invalidateCache(uniqueTargets, provider);
    }
  }

  /**
   * Gets environment variable safely
   * @param {string} name - Environment variable name
   * @returns {string|undefined} Environment variable value
   */
  getEnvVar(name) {
    if (typeof process !== 'undefined' && process.env) {
      return process.env[name];
    }
    return undefined;
  }

  /**
   * Simple hash function for generating ETags
   * @param {string} str - String to hash
   * @returns {string} Hash value
   */
  simpleHash(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * Checks if conditional request headers indicate cache is still valid
   * @param {Object} requestHeaders - Incoming request headers
   * @param {Object} cacheHeaders - Generated cache headers
   * @returns {boolean} Whether cache is still valid
   */
  isConditionalRequestValid(requestHeaders, cacheHeaders) {
    const ifNoneMatch = requestHeaders['if-none-match'];
    const ifModifiedSince = requestHeaders['if-modified-since'];

    // Check ETag
    if (ifNoneMatch && cacheHeaders['ETag']) {
      return ifNoneMatch === cacheHeaders['ETag'];
    }

    // Check Last-Modified
    if (ifModifiedSince && cacheHeaders['Last-Modified']) {
      const clientDate = new Date(ifModifiedSince);
      const lastModified = new Date(cacheHeaders['Last-Modified']);
      return clientDate >= lastModified;
    }

    return false;
  }

  /**
   * Gets cache statistics
   * @returns {Object} Cache performance stats
   */
  getCacheStats() {
    return {
      queuedInvalidations: this.invalidationQueue.length,
      cacheHeadersGenerated: this.cacheHeaders.size,
      supportedProviders: Object.keys(this.CDN_CONFIG),
      cacheTypes: Object.keys(this.CACHE_DURATIONS),
    };
  }
}
